import React, { useEffect } from 'react';
import { McSelect, McLoadingIndicator } from '@maersk-global/mds-react-wrapper';
import { useQuery } from '@tanstack/react-query';
import { registryService } from '../../services/registryService';
import { DbConnection } from '../../types/registry';

interface ConnectionSelectProps {
    value: string;
    onChange: (connectionId: string, connection?: DbConnection) => void;
    onResolvedConnection?: (connection?: DbConnection) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    style?: React.CSSProperties;
}

export const ConnectionSelect: React.FC<ConnectionSelectProps> = ({
    value,
    onChange,
    onResolvedConnection,
    label = "External Connection",
    placeholder = "Select connection",
    required = false,
    disabled = false,
    style,
}) => {
    const { data: connections = [], isLoading } = useQuery({
        queryKey: ['registry-connections-active'],
        queryFn: () => registryService.getConnections(false),
        staleTime: 30_000,
    });

    const options = connections.map((conn) => ({
        label: `${conn.connectionName} (${conn.serverType} — ${conn.databaseName})`,
        value: conn.id,
    }));

    useEffect(() => {
        if (!onResolvedConnection) return;
        if (!value) {
            onResolvedConnection(undefined);
            return;
        }
        onResolvedConnection(connections.find((c) => c.id === value));
    }, [value, connections, onResolvedConnection]);

    if (isLoading) {
        return <McLoadingIndicator fit="small" />;
    }

    return (
        <McSelect
            label={label}
            placeholder={placeholder}
            value={value}
            data={options}
            optionselected={(e: any) => {
                const selectedId = e.detail?.value || e.detail;
                const selectedConn = connections.find((c) => c.id === selectedId);
                onChange(selectedId, selectedConn);
            }}
            required={required}
            disabled={disabled}
            style={style}
        />
    );
};
